import type { StatsView } from '@tm/shared';
import { BarChart, LineChart, type Point } from '../components/Charts.js';
import { Heatmap } from '../components/Heatmap.js';

const dayLabel = (iso: string) => {
  const [, m, d] = iso.split('-');
  return `${d}.${m}`;
};

export function Stats({ stats }: { stats: StatsView }) {
  const last14 = stats.daily.slice(-14);

  const bars: Point[] = last14.map((d) => ({
    label: dayLabel(d.date),
    value: d.attempts,
    hint: `${dayLabel(d.date)}: ${d.attempts} примеров`,
  }));

  // В линию точности берём только дни, когда были занятия: рисовать 0% за день
  // отдыха — значит показывать провал там, где его не было.
  const active = stats.daily.filter((d) => d.attempts > 0).slice(-14);
  const accuracy: Point[] = active.map((d) => ({
    label: dayLabel(d.date),
    value: d.accuracy,
    hint: `${dayLabel(d.date)}: ${Math.round(d.accuracy * 100)}% из ${d.attempts}`,
  }));

  const speed: Point[] = active
    .filter((d) => d.avgMs > 0)
    .map((d) => ({
      label: dayLabel(d.date),
      value: d.avgMs / 1000,
      hint: `${dayLabel(d.date)}: ${(d.avgMs / 1000).toFixed(1)} с на ответ`,
    }));
  const speedMax = Math.max(1, ...speed.map((p) => p.value));

  return (
    <div className="stack">
      <h1>Прогресс</h1>

      <div className="tiles">
        <div className="tile">
          <div className="v">{stats.totalCorrect}</div>
          <div className="k">решено примеров</div>
        </div>
        <div className="tile">
          <div className="v">{Math.round(stats.accuracy * 100)}%</div>
          <div className="k">точность</div>
        </div>
        <div className="tile">
          <div className="v">{(stats.avgMs / 1000).toFixed(1)} с</div>
          <div className="k">среднее время</div>
        </div>
        <div className="tile">
          <div className="v">{stats.currentStreakDays} 🔥</div>
          <div className="k">дней подряд (рекорд {stats.bestStreakDays})</div>
        </div>
      </div>

      <div className="card">
        <div className="row between" style={{ marginBottom: 6 }}>
          <h3>Карта таблицы</h3>
          <span className="small dim">{stats.masteredCount} из {stats.factCount} освоено</span>
        </div>
        <Heatmap cells={stats.heat} />
      </div>

      <div className="card">
        <h3>Примеров в день</h3>
        <p className="small dim" style={{ margin: '2px 0 8px' }}>Последние две недели</p>
        <BarChart points={bars} format={(v) => String(Math.round(v))} />
      </div>

      <div className="card">
        <h3>Точность</h3>
        <p className="small dim" style={{ margin: '2px 0 8px' }}>Только дни с занятиями</p>
        <LineChart points={accuracy} max={1} min={0.5} format={(v) => `${Math.round(v * 100)}%`} />
      </div>

      {speed.length >= 2 && (
        <div className="card">
          <h3>Скорость ответа</h3>
          <p className="small dim" style={{ margin: '2px 0 8px' }}>Чем ниже линия, тем быстрее</p>
          <LineChart points={speed} max={speedMax} format={(v) => `${v.toFixed(1)}с`} />
        </div>
      )}

      {stats.weakest.length > 0 && (
        <div className="card">
          <h3>Над чем поработать</h3>
          <table className="table-view" style={{ marginTop: 6 }}>
            <thead>
              <tr>
                <th>Пример</th>
                <th className="num">Ошибок</th>
                <th className="num">Время</th>
              </tr>
            </thead>
            <tbody>
              {stats.weakest.map((c) => (
                <tr key={c.key}>
                  <td><b>{c.a} × {c.b} = {c.a * c.b}</b></td>
                  <td className="num">{c.wrong} из {c.total}</td>
                  <td className="num">{(c.avgMs / 1000).toFixed(1)} с</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="small dim" style={{ marginBottom: 0 }}>
            Эти примеры будут попадаться чаще, пока не закрепятся.
          </p>
        </div>
      )}
    </div>
  );
}
